/**
 * IndexedDB Collection Storage Manager
 * 
 * Single source of truth for CRUD collection storage and management.
 * Replaces localStorage-based collectionStorageManager for better scalability and performance.
 * 
 * Usage:
 * - Direct: import indexedDbCollectionStorage from './utils/indexedDbCollectionStorage'
 * - Unified: import storageManager from './utils/storageManager' (recommended)
 * 
 * Features:
 * - All collection operations use IndexedDB
 * - No localStorage dependency
 * - Better quota handling (50MB+ vs 5MB)
 * - Atomic transactions
 * - Efficient querying with indexes
 * - Support for collection versioning and soft deletes
 * - Syncing to crud-service-universal is handled by Canvas.jsx after merging with config
 */

import {syncCollectionToCrud} from './crudServiceSync';

const DB_NAME = 'CollectionStorage';
const DB_VERSION = 1;
const COLLECTIONS_STORE = 'collections';

class IndexedDbCollectionStorage {
  constructor() {
    this.db = null;
    this.isSupported = typeof indexedDB !== 'undefined';
    this.initPromise = null;
  }

  /**
   * Initialize the database connection with proper schema
   * @returns {Promise<IDBDatabase>}
   */
  async init() {
    if (this.db) {
      return this.db;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    if (!this.isSupported) {
      throw new Error('IndexedDB is not supported in this browser');
    }

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('❌ Failed to open IndexedDB:', request.error); /* eslint-disable-line no-console */
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('✅ IndexedDB collection storage initialized'); /* eslint-disable-line no-console */
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // Create collections store with indexes
        if (!db.objectStoreNames.contains(COLLECTIONS_STORE)) {
          const collectionsStore = db.createObjectStore(COLLECTIONS_STORE, {keyPath: 'id'});
          
          // Indexes for efficient querying
          collectionsStore.createIndex('projectId', 'projectId', {unique: false});
          collectionsStore.createIndex('collectionName', 'collectionName', {unique: false});
          collectionsStore.createIndex('deleted', 'deleted', {unique: false});
          collectionsStore.createIndex('isNew', 'isNew', {unique: false});
          collectionsStore.createIndex('lastModified', 'lastModified', {unique: false});
          collectionsStore.createIndex('projectCollection', ['projectId', 'collectionName'], {unique: true});
          
          console.log('📦 Created collections object store with indexes'); /* eslint-disable-line no-console */
        }
      };
    });

    return this.initPromise;
  }

  /**
   * Generate unique ID for collection storage
   * @param {string} projectId
   * @param {string} collectionName
   * @returns {string}
   */
  generateCollectionId(projectId, collectionName) {
    return `${projectId}::${collectionName}`;
  }

  /**
   * Load all collections for a project
   * @param {string} projectId
   * @returns {Promise<object>} Collections object {collectionName: collectionData}
   */
  async loadCollections(projectId) {
    if (!projectId) return {};
    
    try {
      await this.init();

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([COLLECTIONS_STORE], 'readonly');
        const store = transaction.objectStore(COLLECTIONS_STORE);
        const index = store.index('projectId');
        const request = index.getAll(projectId);

        request.onsuccess = () => {
          const collections = {};
          (request.result || []).forEach(collection => {
            collections[collection.collectionName] = collection;
          });
          
          // eslint-disable-next-line no-console
          console.log(`📊 Loaded ${Object.keys(collections).length} collections from IndexedDB for project:`, projectId);
          
          // Note: Syncing to crud-service-universal is now handled in Canvas.jsx after merging with config
          
          resolve(collections);
        };

        request.onerror = () => {
          console.error('❌ Failed to load collections:', request.error); /* eslint-disable-line no-console */
          reject(request.error);
        };
      });
    } catch (err) {
      console.error('Failed to load collections:', err); /* eslint-disable-line no-console */
      return {};
    }
  }

  /**
   * Save all collections for a project
   * @param {string} projectId
   * @param {object} collections - Collections object {collectionName: collectionData}
   * @returns {Promise<boolean>}
   */
  async saveCollections(projectId, collections) {
    if (!projectId) return false;
    
    try {
      await this.init();

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([COLLECTIONS_STORE], 'readwrite');
        const store = transaction.objectStore(COLLECTIONS_STORE);
        
        let completed = 0;
        const total = Object.keys(collections).length;

        if (total === 0) {
          resolve(true);
          return;
        }

        Object.entries(collections).forEach(([name, data]) => {
          const collectionData = {
            id: this.generateCollectionId(projectId, name),
            projectId,
            collectionName: name,
            name,
            lastModified: Date.now(),
            ...data
          };

          const request = store.put(collectionData);

          request.onsuccess = () => {
            completed++;
            if (completed === total) {
              // eslint-disable-next-line no-console
              console.log(`💾 Saved ${total} collections to IndexedDB for project:`, projectId);
              resolve(true);
            }
          };

          request.onerror = () => {
            console.error('❌ Failed to save collection:', request.error); /* eslint-disable-line no-console */
            reject(request.error);
          };
        });
      });
    } catch (err) {
      console.error('Failed to save collections:', err); /* eslint-disable-line no-console */
      return false;
    }
  }

  /**
   * Get a single collection
   * @param {string} projectId
   * @param {string} collectionName
   * @returns {Promise<object|null>}
   */
  async getCollection(projectId, collectionName) {
    try {
      await this.init();

      const id = this.generateCollectionId(projectId, collectionName);

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([COLLECTIONS_STORE], 'readonly');
        const store = transaction.objectStore(COLLECTIONS_STORE);
        const request = store.get(id);

        request.onsuccess = () => {
          resolve(request.result || null);
        };

        request.onerror = () => {
          console.error('❌ Failed to get collection:', request.error); /* eslint-disable-line no-console */
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error getting collection:', error); /* eslint-disable-line no-console */
      return null;
    }
  }

  /**
   * Save a single collection
   * @param {string} projectId
   * @param {string} collectionName
   * @param {object} collectionData
   * @returns {Promise<boolean>}
   */
  async saveCollection(projectId, collectionName, collectionData) {
    try {
      await this.init();

      const id = this.generateCollectionId(projectId, collectionName);
      const data = {
        id,
        projectId,
        collectionName,
        name: collectionName,
        lastModified: Date.now(),
        ...collectionData
      };

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([COLLECTIONS_STORE], 'readwrite');
        const store = transaction.objectStore(COLLECTIONS_STORE);
        const request = store.put(data);

        request.onsuccess = () => {
          // eslint-disable-next-line no-console
          console.log(`💾 Saved collection to IndexedDB: ${collectionName}`);
          
          // Auto-sync to crud-service-universal
          syncCollectionToCrud(projectId, collectionName, collectionData).catch(err => {
            console.warn(`⚠️ Failed to sync collection ${collectionName} to crud-service-universal:`, err); /* eslint-disable-line no-console */
          });
          
          resolve(true);
        };

        request.onerror = () => {
          console.error('❌ Failed to save collection:', request.error); /* eslint-disable-line no-console */
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error saving collection:', error); /* eslint-disable-line no-console */
      return false;
    }
  }

  /**
   * Delete a collection
   * @param {string} projectId
   * @param {string} collectionName
   * @returns {Promise<boolean>}
   */
  async deleteCollection(projectId, collectionName) {
    try {
      await this.init();

      const id = this.generateCollectionId(projectId, collectionName);

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([COLLECTIONS_STORE], 'readwrite');
        const store = transaction.objectStore(COLLECTIONS_STORE);
        const request = store.delete(id);

        request.onsuccess = () => {
          // eslint-disable-next-line no-console
          console.log(`🗑️ Deleted collection from IndexedDB: ${collectionName}`);
          resolve(true);
        };

        request.onerror = () => {
          console.error('❌ Failed to delete collection:', request.error); /* eslint-disable-line no-console */
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error deleting collection:', error); /* eslint-disable-line no-console */
      return false;
    }
  }

  /**
   * Mark a collection as deleted (soft delete)
   * @param {string} projectId
   * @param {string} collectionName
   * @returns {Promise<boolean>}
   */
  async markCollectionAsDeleted(projectId, collectionName) {
    const collection = await this.getCollection(projectId, collectionName);
    if (!collection) return false;

    collection.deleted = true;
    collection.lastModified = Date.now();
    
    return this.saveCollection(projectId, collectionName, collection);
  }

  /**
   * Check if project has any modified collections
   * @param {string} projectId
   * @returns {Promise<boolean>}
   */
  async hasCollectionModifications(projectId) {
    const collections = await this.loadCollections(projectId);
    return Object.keys(collections).length > 0;
  }

  /**
   * Merge IndexedDB collections with configuration collections
   * IndexedDB collections take precedence
   * @param {string} projectId
   * @param {object} configCollections - Collections from configuration
   * @returns {Promise<object>} Merged collections
   */
  async mergeWithConfiguration(projectId, configCollections) {
    const localCollections = await this.loadCollections(projectId);
    
    // Start with configuration collections
    const merged = {...configCollections};
    
    // Overlay with local modifications
    Object.entries(localCollections).forEach(([name, collection]) => {
      merged[name] = collection;
    });
    
    return merged;
  }

  /**
   * Clear all collections for a project
   * @param {string} projectId
   * @returns {Promise<boolean>}
   */
  async clearCollections(projectId) {
    if (!projectId) return false;
    
    try {
      await this.init();

      const collections = await this.loadCollections(projectId);
      const collectionNames = Object.keys(collections);

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([COLLECTIONS_STORE], 'readwrite');
        const store = transaction.objectStore(COLLECTIONS_STORE);
        
        let completed = 0;
        const total = collectionNames.length;

        if (total === 0) {
          resolve(true);
          return;
        }

        collectionNames.forEach(name => {
          const id = this.generateCollectionId(projectId, name);
          const request = store.delete(id);

          request.onsuccess = () => {
            completed++;
            if (completed === total) {
              // eslint-disable-next-line no-console
              console.log('🗑️ Cleared collections from IndexedDB for project:', projectId);
              resolve(true);
            }
          };

          request.onerror = () => {
            console.error('❌ Failed to clear collection:', request.error); /* eslint-disable-line no-console */
            reject(request.error);
          };
        });
      });
    } catch (err) {
      console.error('Failed to clear collections:', err); /* eslint-disable-line no-console */
      return false;
    }
  }

  /**
   * Get collections that are marked as new
   * @param {string} projectId
   * @returns {Promise<object>}
   */
  async getNewCollections(projectId) {
    const collections = await this.loadCollections(projectId);
    return Object.entries(collections)
      .filter(([, data]) => data.isNew === true)
      .reduce((acc, [name, data]) => ({...acc, [name]: data}), {});
  }

  /**
   * Get collections that are marked as deleted
   * @param {string} projectId
   * @returns {Promise<object>}
   */
  async getDeletedCollections(projectId) {
    const collections = await this.loadCollections(projectId);
    return Object.entries(collections)
      .filter(([, data]) => data.deleted === true)
      .reduce((acc, [name, data]) => ({...acc, [name]: data}), {});
  }

  /**
   * Export collections as JSON string
   * @param {string} projectId
   * @returns {Promise<string>}
   */
  async exportCollections(projectId) {
    const collections = await this.loadCollections(projectId);
    return JSON.stringify(collections, null, 2);
  }

  /**
   * Import collections from JSON string
   * @param {string} projectId
   * @param {string} jsonString
   * @returns {Promise<boolean>}
   */
  async importCollections(projectId, jsonString) {
    try {
      const collections = JSON.parse(jsonString);
      return this.saveCollections(projectId, collections);
    } catch (err) {
      console.error('Failed to import collections:', err); /* eslint-disable-line no-console */
      return false;
    }
  }

  /**
   * Migrate from localStorage collectionStorageManager
   * @param {string} projectId
   * @returns {Promise<number>} Number of collections migrated
   */
  async migrateFromLocalStorage(projectId) {
    try {
      const storageKey = `collections_${projectId}`;
      const localData = localStorage.getItem(storageKey);
      
      if (!localData) {
        return 0;
      }

      console.log(`🔄 Migrating collections from localStorage for project: ${projectId}`); /* eslint-disable-line no-console */
      
      const collections = JSON.parse(localData);
      const collectionCount = Object.keys(collections).length;
      
      if (collectionCount > 0) {
        await this.saveCollections(projectId, collections);
        
        // Remove from localStorage after successful migration
        localStorage.removeItem(storageKey);
        
        // eslint-disable-next-line no-console
        console.log(`✅ Migrated ${collectionCount} collections from localStorage to IndexedDB`);
      }
      
      return collectionCount;
    } catch (error) {
      console.error('Error migrating collections from localStorage:', error); /* eslint-disable-line no-console */
      return 0;
    }
  }

  /**
   * Check if collection exists
   * @param {string} projectId
   * @param {string} collectionName
   * @returns {Promise<boolean>}
   */
  async collectionExists(projectId, collectionName) {
    const collection = await this.getCollection(projectId, collectionName);
    return !!collection;
  }

  /**
   * Get all collection names for a project
   * @param {string} projectId
   * @returns {Promise<Array<string>>}
   */
  async getCollectionNames(projectId) {
    const collections = await this.loadCollections(projectId);
    return Object.keys(collections);
  }

  /**
   * Check if IndexedDB is supported and available
   * @returns {boolean}
   */
  isAvailable() {
    return this.isSupported;
  }
}

// Export singleton instance
const indexedDbCollectionStorage = new IndexedDbCollectionStorage();

export default indexedDbCollectionStorage;
